import type { Registry as Services } from '@ember/service';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';

const ROUTES = {
  HOTEL: {
    RATES: 'hotel.rates.index',
    ERROR: 'hotel.error',
    BUDGET: 'hotel.budget',
    BUDGET_DETAIL: 'hotel.settings.budget.detail',
    FORECAST: 'hotel.settings.forecast.detail',
    FORECAST_VIEW: 'hotel.forecast',
    EVENTS: 'hotel.events',
    STRATEGY: 'hotel.strategy',
  },
  MARKET: {
    SMART_INSIGHTS: 'market.smart-insights',
    MARKET_RATES: 'market.marketrates.index',
    ERROR: 'market.error',
  },
  DESTINATION: {
    INDEX: 'destination.index',
    REPORTING_DASHBOARD: 'destination.reporting.dashboard',
  },
} as const;

type QueryParams = Record<string, string | undefined>;

interface SwitchAccountServices {
  access: Services['access'];
  router: RouterServiceWithLegacyTransitionTo;
}

interface SwitchAccountTarget {
  id: string;
  type: 'destination' | 'hotel' | 'market';
}

function switchToDestination(
  id: string,
  currentRouteName: string,
  queryParams: QueryParams,
  { router }: SwitchAccountServices,
): void {
  let targetRoute: string = currentRouteName;
  let finalQueryParams = { ...queryParams };

  // Check if we're in a destination route
  if (!currentRouteName.includes('destination.')) {
    finalQueryParams = {};
    targetRoute = ROUTES.DESTINATION.INDEX;
  }

  if (targetRoute === ROUTES.DESTINATION.REPORTING_DASHBOARD) {
    // if switching destination sub while in dashboard, go to main index
    router.legacyTransitionTo(ROUTES.DESTINATION.INDEX, id);
    return;
  }

  router.legacyTransitionTo(targetRoute, id, {
    queryParams: finalQueryParams,
  });
}

function switchToHotel(
  id: string,
  currentRouteName: string,
  queryParams: QueryParams,
  { router }: SwitchAccountServices,
): void {
  let targetRoute: string = currentRouteName;
  let finalQueryParams = { ...queryParams };

  // Check if we're in a hotel route
  if (!currentRouteName.includes('hotel.')) {
    finalQueryParams = {};
    targetRoute = ROUTES.HOTEL.RATES;
  }

  // Handle spider-dashboard route (only when staying within hotel routes)
  if (
    currentRouteName.includes('hotel.') &&
    currentRouteName.includes('.spider-dashboard')
  ) {
    const currentRoute = router.currentRoute;
    if (currentRoute) {
      const params = currentRoute.params as Record<string, string>;
      router.transitionTo(currentRouteName, id, params['path'], {
        queryParams,
      });
      return;
    }
  }

  // Budget & Forecast detail routes - reset entry_id
  if (
    currentRouteName === ROUTES.HOTEL.BUDGET_DETAIL ||
    currentRouteName === ROUTES.HOTEL.FORECAST
  ) {
    router.legacyTransitionTo(
      targetRoute,
      id,
      { entry_id: undefined },
      { queryParams: finalQueryParams },
    );
    return;
  }

  // Budget & Forecast view mode - reset entryId query param
  if (
    currentRouteName === ROUTES.HOTEL.BUDGET ||
    currentRouteName === ROUTES.HOTEL.FORECAST_VIEW
  ) {
    router.legacyTransitionTo(targetRoute, id, {
      queryParams: { ...finalQueryParams, entryId: undefined },
    });
    return;
  }

  // Handle error routes
  if (
    currentRouteName === ROUTES.HOTEL.ERROR ||
    currentRouteName === ROUTES.MARKET.ERROR
  ) {
    router.legacyTransitionTo(ROUTES.HOTEL.RATES, id, {
      queryParams: { reason: null },
    });
    return;
  }

  // Events query params (eventsStatuses/eventsCategories/month/highlight) are all
  // controller-scoped, so Ember preserves them across the hotel switch on its own.
  // Forwarding them re-serializes the array params into double-encoded strings that
  // crash on deserialization (SH-49619), so switch without forwarding here.
  if (currentRouteName.startsWith(ROUTES.HOTEL.EVENTS)) {
    router.legacyTransitionTo(targetRoute, id);
    return;
  }

  // The strategy `competitors` query param is controller-scoped, so Ember
  // preserves it across the hotel switch on its own. Forwarding the already-
  // serialized array re-serializes it into a double-encoded string that crashes
  // on deserialization (SH-51051, same class as SH-49619), so switch without
  // forwarding here.
  if (currentRouteName.startsWith(ROUTES.HOTEL.STRATEGY)) {
    router.legacyTransitionTo(targetRoute, id);
    return;
  }

  router.legacyTransitionTo(targetRoute, id, {
    queryParams: finalQueryParams,
  });
}

function switchToMarket(
  id: string,
  currentRouteName: string,
  queryParams: QueryParams,
  { access, router }: SwitchAccountServices,
): void {
  const defaultProductRoute = access.hasSmartInsights
    ? ROUTES.MARKET.SMART_INSIGHTS
    : ROUTES.MARKET.MARKET_RATES;

  let targetRoute: string = currentRouteName;
  let finalQueryParams = { ...queryParams };

  // Check if we're in a market route
  if (!currentRouteName.includes('market.')) {
    finalQueryParams = {};
    targetRoute = defaultProductRoute;
  }

  // Reset page parameter on market switch
  if (finalQueryParams['page']) {
    finalQueryParams['page'] = '1';
  }

  // Handle spider-dashboard route (only when staying within market routes)
  if (
    currentRouteName.includes('market.') &&
    currentRouteName.includes('.spider-dashboard')
  ) {
    const currentRoute = router.currentRoute;
    if (currentRoute) {
      const params = currentRoute.params as Record<string, string>;
      router.transitionTo(currentRouteName, id, params['path'], {
        queryParams,
      });
      return;
    }
  }

  // Handle error routes
  if (
    currentRouteName === ROUTES.HOTEL.ERROR ||
    currentRouteName === ROUTES.MARKET.ERROR
  ) {
    router.legacyTransitionTo(defaultProductRoute, id, {
      queryParams: { reason: null },
    });
    return;
  }

  router.legacyTransitionTo(targetRoute, id, {
    queryParams: finalQueryParams,
  });
}

/**
 * Lumen's rules for switching the account in view, shared by the Ember hotel
 * selection dropdown and the React header's account selector.
 */
export default function switchAccount(
  target: SwitchAccountTarget,
  services: SwitchAccountServices,
): void {
  const currentRouteName = services.router.currentRouteName ?? '';
  const queryParams = (services.router.currentRoute?.queryParams ??
    {}) as QueryParams;

  if (target.type === 'market') {
    switchToMarket(target.id, currentRouteName, queryParams, services);
    return;
  }

  if (target.type === 'hotel') {
    switchToHotel(target.id, currentRouteName, queryParams, services);
    return;
  }

  switchToDestination(target.id, currentRouteName, queryParams, services);
}
